import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import {
  CalendarDaysIcon,
  ClockIcon,
  MapPinIcon,
  UserGroupIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

const EventsList = () => {
  const navigate = useNavigate();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Sample events data extracted and enhanced from Homepage.js calendar section
  const [events] = useState([
    {
      id: 1,
      title: "Independence Day Celebration",
      date: "2025-08-15",
      time: "09:00 AM",
      location: "Apollo Main Campus",
      category: "National Holiday",
      attendees: 500,
      description: "Join us for a special Independence Day celebration with cultural programs and team activities.",
      status: "upcoming",
      color: "red",
      organizer: "HR Department"
    },
    {
      id: 2,
      title: "Advanced Cardiac Surgery Techniques",
      date: "2025-02-15",
      time: "10:00 AM",
      location: "Apollo Medical Center",
      category: "Medical Training",
      attendees: 45,
      description: "Learn the latest techniques in cardiac surgery from our expert surgeons.",
      status: "upcoming",
      color: "blue",
      organizer: "Dr. Sharma"
    },
    {
      id: 3,
      title: "Digital Health Innovation Summit",
      date: "2025-03-25",
      time: "09:30 AM",
      location: "Convention Center",
      category: "Conference",
      attendees: 150,
      description: "Explore the future of digital health and medical technology innovations.",
      status: "upcoming",
      color: "purple",
      organizer: "Tech Department"
    },
    {
      id: 4,
      title: "Mawlid Celebration",
      date: "2025-09-15",
      time: "11:00 AM",
      location: "Apollo Community Hall",
      category: "Religious Festival",
      attendees: 300,
      description: "Join us for the Mawlid celebration with community gathering and special prayers.",
      status: "upcoming",
      color: "green",
      organizer: "Cultural Committee"
    },
    {
      id: 5,
      title: "Healthcare Innovation Workshop",
      date: "2025-04-10",
      time: "02:00 PM",
      location: "Training Center",
      category: "Workshop",
      attendees: 60,
      description: "Interactive workshop on latest healthcare innovations and best practices.",
      status: "upcoming",
      color: "orange",
      organizer: "Training Department"
    }
  ]);

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'National Holiday', label: 'National Holiday' },
    { value: 'Medical Training', label: 'Medical Training' },
    { value: 'Conference', label: 'Conference' },
    { value: 'Religious Festival', label: 'Religious Festival' },
    { value: 'Workshop', label: 'Workshop' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'ongoing', label: 'Ongoing' },
    { value: 'completed', label: 'Completed' }
  ];

  // Filter events based on search term, category, and status
  const filteredEvents = events.filter(event => {
    const matchesSearch = event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.organizer.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = categoryFilter === 'all' || event.category === categoryFilter;
    const matchesStatus = statusFilter === 'all' || event.status === statusFilter;
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const getColorClasses = (color) => {
    const colorMap = {
      red: 'bg-red-100 text-red-800 border-red-200',
      blue: 'bg-blue-100 text-blue-800 border-blue-200',
      green: 'bg-green-100 text-green-800 border-green-200',
      purple: 'bg-purple-100 text-purple-800 border-purple-200',
      orange: 'bg-orange-100 text-orange-800 border-orange-200',
    };
    return colorMap[color] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Component */}
      <Header />

      {/* Events Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Events</h1>
              <p className="text-gray-600 mt-1 text-sm sm:text-base">Discover and join upcoming events</p>
            </div>
            <div className="flex items-center">
              <button className="px-3 sm:px-4 py-2 bg-apollo-accent text-white rounded-lg hover:bg-apollo-primary transition-colors font-medium text-sm sm:text-base">
                Create Event
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Sub Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-4 sm:space-x-8 overflow-x-auto">
            <button 
              onClick={() => navigate('/events')}
              className="border-b-2 border-apollo-accent text-apollo-accent py-4 px-1 text-sm font-medium whitespace-nowrap"
            >
              All Events
            </button>
            <button 
              onClick={() => navigate('/events/categories')}
              className="border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 py-4 px-1 text-sm font-medium transition-colors whitespace-nowrap"
            >
              Categories
            </button>
            <button 
              onClick={() => navigate('/events/my-events')}
              className="border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 py-4 px-1 text-sm font-medium transition-colors whitespace-nowrap"
            >
              My Events
            </button>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search events..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-apollo-accent focus:border-transparent"
                />
              </div>
            </div>

            {/* Category Filter */}
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-apollo-accent focus:border-transparent"
            >
              {categories.map(category => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-apollo-accent focus:border-transparent"
            >
              {statusOptions.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Events Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {filteredEvents.length === 0 ? (
            <div className="col-span-full text-center py-8 sm:py-12">
              <CalendarDaysIcon className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg sm:text-xl font-medium text-gray-900 mb-2">No Events Found</h3>
              <p className="text-sm sm:text-base text-gray-600">Try adjusting your search or filters to find events.</p>
            </div>
          ) : (
            filteredEvents.map(event => (
              <div key={event.id} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 cursor-pointer">
                <div className="p-4 sm:p-6">
                  <div className="flex items-start justify-between mb-3 sm:mb-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                        {event.title}
                      </h3>
                      <span className={`inline-block px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium border ${getColorClasses(event.color)}`}>
                        {event.category}
                      </span>
                    </div>
                  </div>

                  <p className="text-gray-600 text-sm mb-3 sm:mb-4 line-clamp-2">
                    {event.description}
                  </p>

                  <div className="space-y-2 text-sm text-gray-500 mb-3 sm:mb-4">
                    <div className="flex items-center space-x-2">
                      <CalendarDaysIcon className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{new Date(event.date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <ClockIcon className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{event.time}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <MapPinIcon className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{event.location}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <UserGroupIcon className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{event.attendees} attendees</span>
                    </div>
                  </div>

                  <div className="border-t pt-3 sm:pt-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs text-gray-500 truncate">
                        By {event.organizer}
                      </div>
                      <div className="flex-shrink-0">
                        <button
                          onClick={() => navigate(`/events/${event.id}`)}
                          className="text-apollo-accent hover:text-apollo-primary text-sm font-medium transition-colors whitespace-nowrap"
                        >
                          View Details →
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between mt-6 sm:mt-8 gap-4">
          <div className="flex items-center text-sm text-gray-600">
            Showing {filteredEvents.length} of {events.length} events
          </div>
          <div className="flex items-center space-x-1 sm:space-x-2">
            <button className="p-2 sm:px-3 sm:py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
            <button className="px-3 py-2 bg-apollo-accent text-white rounded-lg text-sm">1</button>
            <button className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm">2</button>
            <button className="p-2 sm:px-3 sm:py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Footer Component */}
      <Footer />
    </div>
  );
};

export default EventsList;
